import { useEffect, useRef, useState } from "react";
import { useAsync } from "react-use";
import { SurveyModel } from "survey-core";

import { DateTime } from "@bps/date-time";

import { useTrackAnalyticsEvent } from "../analytics/app-insights.hooks";
import { AnalyticsEvent } from "../analytics/app-insights.types";
import { ErrorResult } from "../api/base";
import { getDocumentDownloadData, getForm } from "../api/form";
import { setupModel } from "./setupModel";
import { FormApiOutput, FormStatus, FormStatusEnum } from "./useFormApi.types";
import { useFormId } from "./useFormId";

export const useFormApi = (): FormApiOutput => {
  const { formId, qrcode, preview } = useFormId();
  // `pin` is the raw text that is entered by the user when they first log in
  // `pin` authenticates getForm via X-Form-AccessCode; `accessToken` comes from getForm response and is used by post/download APIs via X-Form-AccessToken.
  const [pin, setPin] = useState<string | undefined>();
  const logEvent = useTrackAnalyticsEvent();

  // `accessToken` is backend-issued and used for follow-up calls; a ref avoids re-renders when it changes.
  const accessTokenRef = useRef<string | undefined>();
  const [formStatus, setFormStatus] = useState<FormStatus>({
    status: FormStatusEnum.Initialising,
    form: undefined,
    titleFields: undefined
  });

  const { form, isReadOnly, status, testform, variables } = formStatus;

  const setDeveloperPreviewForm = (form: string) => {
    const formParsed = JSON.parse(form);
    setupModel({
      logEvent,
      modelJson: form,
      formDisplaySettings: formParsed.formDisplaySettings,
      isReadOnly: formParsed.isReadOnly,
      variables: formParsed.variables,
      documentDownloadCallBackUrl: formParsed.documentDownloadCallBackUrl,
      formId,
      accessToken: accessTokenRef.current,
      setFormStatus,
      qrcode,
      preview
    });
  };

  // reset pin when form ID changes - probably only will happen when testing
  useEffect(() => {
    setPin(undefined);
    accessTokenRef.current = undefined;
  }, [formId]);

  useEffect(() => {
    setFormStatus({
      status: formId ? FormStatusEnum.Loading : FormStatusEnum.NotFound,
      description: "Incorrect Address",
      helpText: ""
    });
  }, [formId, qrcode]);

  const {
    loading,
    error: _errorWithWrongType,
    value
  } = useAsync(
    !form &&
      status !== FormStatusEnum.DecodeError &&
      status !== FormStatusEnum.Completed &&
      status !== FormStatusEnum.IsReadOnly
      ? () => getForm(formId, logEvent, pin)
      : Promise.resolve,
    [formId, pin]
  );

  // Our error interceptor means the error being returned is our ErrorResult
  //  rather than the typical JavaScript error.  This is probably not the best
  //  way to fix this issue.
  const error = _errorWithWrongType as unknown as ErrorResult | undefined;

  const data = value?.data;
  const responseAccessToken = data?.accessToken ?? data?.AccessToken;

  useEffect(() => {
    if (responseAccessToken && responseAccessToken !== accessTokenRef.current) {
      accessTokenRef.current = responseAccessToken;
    }
  }, [responseAccessToken]);

  // The 'validators' property of form elements is passed from the backend as
  // a string, so it needs to be parsed.
  const formData = JSON.parse(data?.formData || "{}");
  formData?.pages?.forEach((page: any) => {
    page.elements?.forEach((element: any) => {
      if (element.validators && typeof element.validators === "string") {
        element.validators = JSON.parse(element.validators);
      }
    });
  });

  if (data) data.formData = JSON.stringify(formData);

  useEffect(() => {
    try {
      if (
        !form &&
        !loading &&
        !error &&
        data &&
        status !== FormStatusEnum.Completed &&
        status !== FormStatusEnum.QrCode &&
        status !== FormStatusEnum.IsReadOnly
      ) {
        const expiry = DateTime.fromISO(data.expiryDate);

        const submittedDate = data.submittedDate
          ? DateTime.fromISO(data.submittedDate)
          : undefined;

        const responseData = data.responseData;
        const isReadOnly = !!data.readOnly;
        formId &&
          data &&
          data.formData &&
          setupModel({
            logEvent,
            modelJson: data.formData,
            formDisplaySettings: data.formDisplaySettings,
            documentDownloadCallBackUrl: data.documentDownloadCallBackUrl,
            variables: data.variables,
            formConfig: data.formConfiguration,
            isReadOnly,
            responseData,
            expiry,
            submittedDate,
            formId,
            accessToken: responseAccessToken,
            setFormStatus,
            qrcode,
            preview
          });
      } else if (error) {
        if (
          error.status !== FormStatusEnum.AlreadyCompleted &&
          !testform &&
          !isReadOnly
        ) {
          setFormStatus(error);
        }
      }
    } catch {
      logEvent(AnalyticsEvent.formDecodeError, {});
      setFormStatus({
        status: FormStatusEnum.DecodeError,
        description: "This form could not be decoded."
      });
    }
  }, [
    loading,
    data,
    formId,
    form,
    responseAccessToken,
    pin,
    error,
    status,
    qrcode,
    preview,
    isReadOnly,
    testform,
    logEvent
  ]);

  const getDocumentDownloadDataWithId = async () => {
    return await getDocumentDownloadData(formId, accessTokenRef.current);
  };

  // TODO: explain what this is doing
  if (form) {
    form.onUpdateQuestionCssClasses.add((options: SurveyModel) => {
      const classes = options.cssClasses;
      classes.mainRoot = "bp-question-root";
      classes.title = "bp-question-title";
      classes.root += " bp-question-root";
      classes.item += " bp-question-item";
      classes.label += " bp-question-label";
      classes.error += " bp-question-error";
    });
    form.setVariable("_isReadOnly", isReadOnly);
    form.setVariable("_getDocumentDownloadData", () =>
      getDocumentDownloadData(formId, accessTokenRef.current)
    );
    if (variables) {
      Object.keys(formStatus.variables).forEach(key => {
        form.setVariable(key, formStatus.variables[key]);
      });
    }
  }

  return {
    ...formStatus,
    loading,
    setDeveloperPreviewForm,
    submitPin: async (rawPin: string) => {
      await getForm(formId, logEvent, rawPin);
      // Note: setPin won't run if the pin is incorrect, because the API request will throw an error
      //  which gets caught by the form
      setPin(rawPin);
      // TODO after setPin is called, useAsync runs and calls getForm a second time, getting the same information
      //  There probably is a better way to do this to save the result of this getForm so the same API isn't called
      //  a second time unnecessarily
    },
    formId,
    getDocumentDownloadData: getDocumentDownloadDataWithId
  };
};
